import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useParams, useLocation } from "react-router-dom";
import { useState, useEffect, Suspense, lazy } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
const LoginPage = lazy(() => import("@/pages/LoginPage"));
const TailorDashboard = lazy(() => import("@/pages/TailorDashboard"));
const StitchDashboard = lazy(() => import("@/pages/stitch2/StitchDashboard"));
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
const SendPhotoPage = lazy(() => import("@/pages/SendPhotoPage"));
const ReceiptPhotoPage = lazy(() => import("@/pages/ReceiptPhotoPage"));
const BookingsDashboard = lazy(() => import("@/pages/BookingsDashboard"));
const BarberDashboard = lazy(() => import("@/pages/BarberDashboard"));
const BarberLoginPage = lazy(() => import("@/pages/BarberLoginPage"));
const TailorAppPage = lazy(() => import("@/pages/TailorAppPage"));
const PublicBookingPage = lazy(() => import("@/pages/PublicBooking").then((m) => ({ default: m.PublicBookingPage })));
const PublicManagePage = lazy(() => import("@/pages/PublicBooking").then((m) => ({ default: m.PublicManagePage })));
const DryCleanDashboard = lazy(() => import("@/pages/DryCleanDashboard"));
const DriverDashboard = lazy(() => import("@/pages/DriverDashboard"));
const DriverLoginPage = lazy(() => import("@/pages/DriverLoginPage"));
const PublicDryClean = lazy(() => import("@/pages/PublicDryClean"));
const PadelDashboard = lazy(() => import("@/pages/PadelDashboard"));
const PublicPadelPage = lazy(() => import("@/pages/PublicPadel").then((m) => ({ default: m.PublicPadelPage })));
const PublicPadelManagePage = lazy(() => import("@/pages/PublicPadel").then((m) => ({ default: m.PublicPadelManagePage })));
const PublicPadelPackagePage = lazy(() => import("@/pages/PublicPadel").then((m) => ({ default: m.PublicPadelPackagePage })));
const PublicPadelProfile = lazy(() => import("@/pages/PublicPadelProfile"));
const PublicPadelAccessLink = lazy(() => import("@/pages/PublicPadelAccessLink"));
const PublicPadelCrew = lazy(() => import("@/pages/PublicPadelCrew"));
const PublicPadelCrewsTab = lazy(() => import("@/pages/PublicPadelCrewsTab"));
const PublicPadelCrewInvite = lazy(() => import("@/pages/PublicPadelCrewInvite"));
const PublicPadelBook = lazy(() => import("@/pages/PublicPadelBook"));
const PublicPadelGamesList = lazy(() => import("@/pages/PublicPadelGames").then((m) => ({ default: m.PublicPadelGamesList })));
const PublicPadelGameDetail = lazy(() => import("@/pages/PublicPadelGames").then((m) => ({ default: m.PublicPadelGameDetail })));
const PublicPadelMySpot = lazy(() => import("@/pages/PublicPadelGames").then((m) => ({ default: m.PublicPadelMySpot })));
import { authGetItem, authSetItem, authRemoveItem } from "@/lib/authStorage";

// Lightweight per-route loading state while a route's own lazy chunk downloads — shown briefly
// instead of a blank screen; the pulsing wordmark matches the app's existing initial-auth-check
// loading state below so a route-chunk load doesn't look visually different from a cold load.
function RouteFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50" data-testid="route-loading-fallback">
      <img src="/ping-master-logo.png" alt="Ping" className="w-24 h-auto animate-pulse" />
    </div>
  );
}

// Clean public link aliases (/b/:slug, /p/:slug) — these paths have no /api prefix so ingress
// routes them to the frontend, not the backend; this component's only job is to bounce straight
// to the real backend-rendered landing page it's aliasing. No conditional logic (unlike /book/:slug
// and /pickup/:slug's own in-app redirect handling) — always forward, unconditionally.
// Redesign Round 1 — reads the shop's features once to decide old vs new dashboard. Loading or
// any error (features call failing) falls back to today's TailorDashboard, never a blank page.
// pages/TailorDashboard.jsx itself is untouched by this round.
function DashboardRouter({ user, onLogout, onUserUpdate }) {
  const API = process.env.REACT_APP_BACKEND_URL;
  const [decided, setDecided] = useState(false);
  const [newDesign, setNewDesign] = useState(false);
  const [features, setFeatures] = useState({});

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/api/shops/${user.shop_id}/features`)
      .then((res) => {
        if (cancelled) return;
        setFeatures(res.data || {});
        setNewDesign(!!(res.data && res.data.new_design));
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setDecided(true); });
    return () => { cancelled = true; };
  }, [user.shop_id]);

  if (!decided) {
    // Don't mount the old dashboard (and its ~17 background calls) while we're still
    // finding out which dashboard this shop should get — show the loading state instead.
    return <RouteFallback />;
  }
  if (!newDesign) {
    return <Suspense fallback={<RouteFallback />}><TailorDashboard user={user} onLogout={onLogout} onUserUpdate={onUserUpdate} /></Suspense>;
  }
  return <Suspense fallback={<RouteFallback />}><StitchDashboard user={user} features={features} onLogout={onLogout} onUserUpdate={onUserUpdate} /></Suspense>;
}

function CleanLinkRedirect({ apiPrefix }) {
  const { slug } = useParams();
  const API = process.env.REACT_APP_BACKEND_URL;
  useEffect(() => {
    window.location.replace(`${API}/api/${apiPrefix}/${slug}`);
  }, [slug]);
  return <RouteFallback />;
}

// Attach the stored JWT to every request.
// Storage picks sessionStorage (per-tab, prevents the "Tab A admin / Tab B tenant" bleed bug) for a
// regular browser tab, or localStorage for an installed/"Add to Home Screen" app — see authStorage.js.
axios.interceptors.request.use((config) => {
  try {
    const token = authGetItem("ps_token");
    if (token) config.headers.Authorization = `Bearer ${token}`;
  } catch (e) { /* ignore */ }
  return config;
});

// On an expired/invalid session (401), warn once and redirect to login.
let sessionExpiredHandled = false;
axios.interceptors.response.use(
  (res) => res,
  (error) => {
    const status = error?.response?.status;
    const url = error?.config?.url || "";
    // Skip the login endpoints — those 401s are "wrong credentials", handled inline.
    if (status === 401 && !url.includes("/auth/") && !sessionExpiredHandled) {
      sessionExpiredHandled = true;
      let dest = "/";
      try {
        const u = JSON.parse(authGetItem("ps_user") || "null");
        if (u && u.role === "barber") dest = "/barber-login";
        if (u && u.role === "driver") dest = "/driver-login";
      } catch (e) { /* ignore */ }
      authRemoveItem("ps_user");
      authRemoveItem("ps_token");
      toast.error("Session expired, please log in again");
      setTimeout(() => { window.location.href = dest; }, 1200);
    }
    return Promise.reject(error);
  }
);

// Decode a JWT and return true if it has no exp or exp is still in the future
function isTokenValid(token) {
  if (!token) return false;
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    if (!payload.exp) return true;
    return payload.exp * 1000 > Date.now();
  } catch (e) {
    return false;
  }
}

function AppContent() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  // Padel's toasts get a dark-green/lime theme to match its dashboard + public pages —
  // every other vertical keeps the existing default sonner look untouched.
  const isPadel = location.pathname.startsWith("/padel");

  useEffect(() => {
    try {
      const savedUser = authGetItem("ps_user");
      const savedToken = authGetItem("ps_token");
      if (savedUser && savedToken && isTokenValid(savedToken)) {
        setUser(JSON.parse(savedUser));
      } else {
        authRemoveItem("ps_user");
        authRemoveItem("ps_token");
      }
    } catch (e) {
      authRemoveItem("ps_user");
      authRemoveItem("ps_token");
    }
    setLoading(false);
  }, []);

  const handleLogin = (userData, token) => {
    sessionExpiredHandled = false;
    setUser(userData);
    authSetItem("ps_user", JSON.stringify(userData));
    authSetItem("ps_token", token);
  };

  const handleLogout = () => {
    setUser(null);
    authRemoveItem("ps_user");
    authRemoveItem("ps_token");
    navigate("/");
  };

  const handleUserUpdate = (patch) => {
    setUser((prev) => {
      const next = { ...prev, ...patch };
      authSetItem("ps_user", JSON.stringify(next));
      return next;
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <img src="/ping-stitch-logo.png" alt="Ping Stitch" className="w-32 h-auto animate-pulse" />
      </div>
    );
  }

  const getRedirectPath = (u) => {
    if (!u) return "/";
    if (u.role === "admin") return "/admin";
    if (u.role === "barber") return "/barber";
    if (u.role === "driver") return "/driver";
    if (u.role === "tailor") return u.vertical === "bookings" ? "/bookings" : u.vertical === "dry_clean" ? "/dry-clean" : u.vertical === "padel" ? "/padel-admin" : "/dashboard";
    return "/";
  };

  return (
    <div className="App">
      <Routes>
        <Route path="/b/:slug" element={<CleanLinkRedirect apiPrefix="b" />} />
        <Route path="/p/:slug" element={<CleanLinkRedirect apiPrefix="p" />} />
        <Route path="/send-photo/:token" element={<Suspense fallback={<RouteFallback />}><SendPhotoPage /></Suspense>} />
        <Route path="/receipt-photo/:token" element={<Suspense fallback={<RouteFallback />}><ReceiptPhotoPage /></Suspense>} />
        <Route path="/book/manage/:token" element={<Suspense fallback={<RouteFallback />}><PublicManagePage /></Suspense>} />
        <Route path="/book/:slug" element={<Suspense fallback={<RouteFallback />}><PublicBookingPage /></Suspense>} />
        <Route path="/pickup/:slug" element={<Suspense fallback={<RouteFallback />}><PublicDryClean /></Suspense>} />
        <Route path="/padel/manage/:token" element={<Suspense fallback={<RouteFallback />}><PublicPadelManagePage /></Suspense>} />
        <Route path="/padel/spot/:token" element={<Suspense fallback={<RouteFallback />}><PublicPadelMySpot /></Suspense>} />
        <Route path="/padel/package/:token" element={<Suspense fallback={<RouteFallback />}><PublicPadelPackagePage /></Suspense>} />
        <Route path="/padel/access/:token" element={<Suspense fallback={<RouteFallback />}><PublicPadelAccessLink /></Suspense>} />
        <Route path="/padel/:slug/profile" element={<Suspense fallback={<RouteFallback />}><PublicPadelProfile /></Suspense>} />
        <Route path="/padel/:slug/me" element={<Suspense fallback={<RouteFallback />}><PublicPadelProfile /></Suspense>} />
        <Route path="/padel/:slug/crew-invite/:inviteToken" element={<Suspense fallback={<RouteFallback />}><PublicPadelCrewInvite /></Suspense>} />
        <Route path="/padel/:slug/crews" element={<Suspense fallback={<RouteFallback />}><PublicPadelCrewsTab /></Suspense>} />
        <Route path="/padel/:slug/crews/:crewId" element={<Suspense fallback={<RouteFallback />}><PublicPadelCrew /></Suspense>} />
        <Route path="/padel/:slug/book" element={<Suspense fallback={<RouteFallback />}><PublicPadelBook /></Suspense>} />
        <Route path="/padel/:slug/games" element={<Suspense fallback={<RouteFallback />}><PublicPadelGamesList /></Suspense>} />
        <Route path="/padel/:slug/games/:bookingId" element={<Suspense fallback={<RouteFallback />}><PublicPadelGameDetail /></Suspense>} />
        <Route path="/padel/:slug" element={<Suspense fallback={<RouteFallback />}><PublicPadelPage /></Suspense>} />
        <Route path="/tailor-app" element={<Suspense fallback={<RouteFallback />}><TailorAppPage /></Suspense>} />
        <Route path="/barber-login" element={<Suspense fallback={<RouteFallback />}>{user && user.role === "barber" ? <Navigate to="/barber" replace /> : <BarberLoginPage onLogin={handleLogin} />}</Suspense>} />
        <Route path="/driver-login" element={<Suspense fallback={<RouteFallback />}>{user && user.role === "driver" ? <Navigate to="/driver" replace /> : <DriverLoginPage onLogin={handleLogin} />}</Suspense>} />
        <Route 
          path="/" 
          element={
            user ? (
              <Navigate to={getRedirectPath(user)} replace />
            ) : (
              <Suspense fallback={<RouteFallback />}><LoginPage onLogin={handleLogin} /></Suspense>
            )
          } 
        />
        <Route 
          path="/bookings" 
          element={
            user && user.role === "tailor" && user.vertical === "bookings" ? (
              <Suspense fallback={<RouteFallback />}><BookingsDashboard user={user} onLogout={handleLogout} onUserUpdate={handleUserUpdate} /></Suspense>
            ) : (
              <Navigate to="/" replace />
            )
          } 
        />
        <Route 
          path="/dry-clean" 
          element={
            user && user.role === "tailor" && user.vertical === "dry_clean" ? (
              <Suspense fallback={<RouteFallback />}><DryCleanDashboard user={user} onLogout={handleLogout} /></Suspense>
            ) : (
              <Navigate to="/" replace />
            )
          } 
        />
        <Route 
          path="/padel-admin" 
          element={
            user && user.role === "tailor" && user.vertical === "padel" ? (
              <Suspense fallback={<RouteFallback />}><PadelDashboard user={user} onLogout={handleLogout} /></Suspense>
            ) : (
              <Navigate to="/" replace />
            )
          } 
        />
        <Route 
          path="/barber" 
          element={
            user && user.role === "barber" ? (
              <Suspense fallback={<RouteFallback />}><BarberDashboard user={user} onLogout={handleLogout} /></Suspense>
            ) : (
              <Navigate to="/barber-login" replace />
            )
          } 
        />
        <Route 
          path="/driver" 
          element={
            user && user.role === "driver" ? (
              <Suspense fallback={<RouteFallback />}><DriverDashboard user={user} onLogout={handleLogout} /></Suspense>
            ) : (
              <Navigate to="/driver-login" replace />
            )
          } 
        />
        <Route 
          path="/admin" 
          element={
            user && user.role === "admin" ? (
              <Suspense fallback={<RouteFallback />}><AdminDashboard user={user} onLogout={handleLogout} /></Suspense>
            ) : (
              <Navigate to="/" replace />
            )
          } 
        />
        <Route 
          path="/dashboard" 
          element={
            user && user.role === "tailor" ? (
              <DashboardRouter user={user} onLogout={handleLogout} onUserUpdate={handleUserUpdate} />
            ) : (
              <Navigate to="/" replace />
            )
          } 
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <Toaster
        position="top-center"
        richColors={isPadel}
        style={isPadel ? {
          "--normal-bg": "#0f2417",
          "--normal-border": "#3f6212",
          "--normal-text": "#ecfccb",
          "--success-bg": "#0f2417",
          "--success-border": "#84cc16",
          "--success-text": "#bef264",
          "--error-bg": "#2a0a0a",
          "--error-border": "#b91c1c",
          "--error-text": "#fecaca",
        } : undefined}
      />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}

export default App;
